import AiKeySettings from './AiKeySettings';
import PolicySettings from './PolicySettings';
import { OssLicenses, EngineLine } from './OssLicenses';
import TtsAdmin from './TtsAdmin';
import TtsNotice from './TtsNotice';
import PcHomeSetting from './PcHomeSetting';
import { StartPref } from './StartChooser';
import FunnelStats from './FunnelStats';
import { FeedbackAdmin, PilotInvite } from './FeedbackAdmin';
import { DripXLibrary } from './DripXLibrary';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { showAllFields, setShowAllFields, FIELDS_TXT } from '../lib/panelCfg';
import { toast } from 'sonner';
import { X, Upload, Trash2, KeyRound, Info, Copy, Smartphone, Wand2, RefreshCw, Ban, Euro, Plus, Mail, MessageCircle, Unlink } from 'lucide-react';
import {
  APP_INFO, GEN_PACKS, DURATIONS, activateCode, getDeviceId, isAdmin, leaveOwner,
  generateCode, listCodes, revokeCode, getLicenses, getContact, saveContact, fill, enterpriseLicense, releaseDevice,
} from '../lib/license';
import { PlayStore, LicenseStatus } from './LicenseParts';
import FeatureProfile from './FeatureProfile';
import PcUnlock from './PcUnlock';
import EnterpriseGen from './EnterpriseGen';
import PlayAdmin from './PlayAdmin';
import DataReset from './DataReset';
import EnterpriseOverview from './EnterpriseOverview';

const ERR = { device: 'licDevice', expired: 'licExpired', revoked: 'licRevoked', offline: 'licOffline', seats: 'entSeatsFull' };

async function copyText(t, txt) {
  try { await navigator.clipboard.writeText(txt); } catch {
    const ta = document.createElement('textarea'); ta.value = txt; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
  }
  toast.success(`${t.copied}: ${txt}`);
}

const untilMs = (u) => Date.UTC(+u.slice(0, 4), +u.slice(4, 6) - 1, +u.slice(6, 8), 23, 59, 59);

function codeStatus(c) {
  if (c.revoked) return 'stRevoked';
  if (!c.activated_at) return 'stUnused';
  if (c.until && untilMs(c.until) < Date.now()) return 'stExpired';
  if (!c.until && c.days && new Date(c.activated_at).getTime() + c.days * 864e5 < Date.now()) return 'stExpired';
  return 'stActive';
}

function LicenseGenerator({ t, onExit }) {
  const [dev, setDev] = useState('');
  const [pack, setPack] = useState('ALL');
  const [days, setDays] = useState(365);
  const [note, setNote] = useState('');
  const [out, setOut] = useState(null);
  const [codes, setCodes] = useState([]);
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(t.locale) : '–');
  const fmtU = (u) => (u ? new Date(untilMs(u)).toLocaleDateString(t.locale) : '–');
  const sendMsg = (o) => fill(t.genSendMsg, { pkg: t['pack_' + o.pack], device: o.device_id, code: o.code, date: fmtU(o.until) });

  const load = async () => {
    try { setCodes(await listCodes()); } catch (e) { toast.error(e.message === 'owner' ? t.ownerExpired : t.licOffline); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const gen = async () => {
    const d = dev.trim().toUpperCase();
    if (!/^GISS-DEV-\d{6}$/.test(d)) return toast.error(t.genBadDev);
    try { setOut(await generateCode(d, pack, days, note)); load(); } catch (e) { toast.error(e.message === 'owner' ? t.ownerExpired : t.licOffline); }
  };
  const release = async (code, d) => {
    if (!window.confirm(t.entConfirmRelease)) return;
    try { await releaseDevice(code, d); load(); } catch { toast.error(t.licOffline); }
  };
  const revoke = async (code) => {
    if (!window.confirm(t.confirmRevoke)) return;
    try { await revokeCode(code); load(); } catch { toast.error(t.licOffline); }
  };

  return (
    <section className="set-sec gen-sec" data-testid="license-generator">
      <h5><Wand2 size={14} /> {t.genTitle}</h5>
      <b>{t.genSingle}</b>
      <div className="row">
        <input type="text" data-testid="gen-device-input" placeholder={t.genDevice} value={dev} onChange={(e) => { setDev(e.target.value); setOut(null); }} />
        <select data-testid="gen-module-select" value={pack} onChange={(e) => setPack(e.target.value)} aria-label={t.genModule}>
          {GEN_PACKS.map((k) => <option key={k} value={k}>{t['pack_' + k]}</option>)}
        </select>
      </div>
      <div className="row">
        <select data-testid="gen-duration-select" value={days} onChange={(e) => setDays(+e.target.value)} aria-label={t.genDuration}>
          {DURATIONS.map((d) => <option key={d} value={d}>{`${t.genDuration}: ${t['dur_' + d]}`}</option>)}
        </select>
        <input type="text" data-testid="gen-note-input" placeholder={t.genNote} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="row">
        <button className="btn btn-proj" data-testid="gen-generate-btn" onClick={gen}><Wand2 size={15} /> {t.genBtn}</button>
        <button className="btn btn-outline btn-sm" data-testid="admin-exit-btn" onClick={onExit}>{t.adminExit}</button>
      </div>
      {out && (
        <div className="device-box gen-out" data-testid="gen-result">
          <div><small>{t.genResult} ({out.device_id} · {t['pack_' + out.pack]} · {t.colUntil} {fmtU(out.until)})</small><b data-testid="gen-code">{out.code}</b></div>
          <button className="btn btn-outline btn-sm" data-testid="gen-copy-btn" onClick={() => copyText(t, out.code)}><Copy size={14} /> {t.copy}</button>
          <button className="btn btn-outline btn-sm" data-testid="gen-send-wa-btn" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(sendMsg(out))}`, '_blank', 'noopener')}><MessageCircle size={14} /></button>
          <button className="btn btn-outline btn-sm" data-testid="gen-send-mail-btn" onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent(APP_INFO.name)}&body=${encodeURIComponent(sendMsg(out))}`; }}><Mail size={14} /></button>
        </div>
      )}
      <PlayAdmin t={t} />
      <FunnelStats t={t} />
      <FeedbackAdmin t={t} />
      <DripXLibrary t={t} />
      <DripXLibrary t={t} agent="stormy" name="Stormy" />
      <PilotInvite t={t} />
      <EnterpriseGen t={t} onCreated={load} copyText={copyText} />
      <EnterpriseOverview t={t} codes={codes} onChanged={load} onRelease={release} />
      <ContactEditor t={t} />
      <div className="gen-list-head">
        <b>{t.genList} ({codes.length})</b>
        <button className="btn btn-outline btn-sm" data-testid="gen-refresh-btn" onClick={load}><RefreshCw size={13} /> {t.refresh}</button>
      </div>
      <div className="gen-table-wrap">
        <table className="gen-table" data-testid="gen-code-list">
          <thead><tr><th>{t.colCode}</th><th>{t.colDevice}</th><th>{t.genModule}</th><th>{t.colUntil}</th><th>{t.colCreated}</th><th>{t.colActivated}</th><th>{t.colStatus}</th><th /></tr></thead>
          <tbody>
            {codes.map((c, i) => (
              <tr key={c.code} data-testid={`gen-code-row-${i}`}>
                <td className="mono">{c.code}{c.note && <small>{c.note}</small>}</td>
                <td className="mono">{c.pack === 'ENT' ? (
                  <div className="ent-devs" data-testid={`gen-ent-devices-${i}`}>
                    <b>{c.company} · {(c.devices || []).length}/{c.seats}</b>
                    {(c.devices || []).map((d) => <span key={d.device_id}>{d.device_id}<button className="icon-btn" aria-label={t.entRelease} data-testid={`gen-ent-release-${d.device_id}`} onClick={() => release(c.code, d.device_id)}><Unlink size={11} /></button></span>)}
                  </div>
                ) : c.device_id}</td>
                <td>{t['pack_' + c.pack]}</td>
                <td>{c.until ? fmtU(c.until) : t['dur_' + c.days]}</td>
                <td>{fmt(c.created_at)}</td>
                <td>{fmt(c.activated_at)}</td>
                <td><span className={`st-chip ${codeStatus(c)}`} data-testid={`gen-code-status-${i}`}>{t[codeStatus(c)]}</span></td>
                <td className="gen-actions">
                  <button className="icon-btn" onClick={() => copyText(t, c.code)} aria-label={t.copy}><Copy size={13} /></button>
                  {!c.revoked && <button className="icon-btn danger" data-testid={`gen-revoke-btn-${i}`} onClick={() => revoke(c.code)} aria-label={t.revoke}><Ban size={13} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ContactEditor({ t }) {
  const [c, setC] = useState(getContact());
  const field = (k, label) => <input type="text" data-testid={`contact-${k}-input`} placeholder={label} value={c[k] || ''} onChange={(e) => setC({ ...c, [k]: e.target.value })} />;
  const save = async () => {
    try { setC({ ...getContact(), ...(await saveContact(c)) }); toast.success(t.contactSaved); } catch (e) { toast.error(e.message === 'owner' ? t.ownerExpired : t.licOffline); }
  };
  return (
    <div data-testid="contact-editor">
      <b>{t.contactTitle}</b>
      <div className="contact-grid">
        {field('name', t.contactName)}{field('phone', t.contactPhone)}{field('email', t.contactEmail)}{field('web', t.contactWeb)}{field('whatsapp', t.contactWa)}{field('play_url', t.contactPlayUrl)}{field('play_test_url', t.contactPlayTest)}
      </div>
      <button className="btn btn-dark btn-sm" data-testid="contact-save-btn" onClick={save}>{t.contactSave}</button>
    </div>
  );
}

function ShowAllFields() {
  const [on, setOn] = useState(showAllFields);
  const [label, hint] = FIELDS_TXT[localStorage.getItem('baustelle_app_lang')] || FIELDS_TXT.en;
  return (
    <label className="row" style={{ gap: 8, alignItems: 'flex-start', cursor: 'pointer' }} data-testid="settings-show-all-fields">
      <input type="checkbox" data-testid="settings-show-all-fields-checkbox" checked={on} onChange={(e) => { setOn(e.target.checked); setShowAllFields(e.target.checked); }} />
      <span><b>{label}</b><br /><small className="muted">{hint}</small></span>
    </label>
  );
}

export default function SettingsDialog({ t, onClose, onChange }) {
  const [lics, setLics] = useState(getLicenses());
  const [code, setCode] = useState('');
  const [admin, setAdmin] = useState(isAdmin());
  const deviceId = getDeviceId();


  const activate = async () => {
    const res = await activateCode(code);
    if (res.error) return toast.error(t[ERR[res.error] || 'licBad'], { duration: 7000 });
    setLics(getLicenses());
    setCode('');
    if (res.admin) { setAdmin(true); toast.success(t.adminOk); } else {
      toast.success(t.licOk + res.mods.map((m) => t['mod_' + m]).join(', ') + (res.exp ? ` (${t.validUntil} ${new Date(res.exp).toLocaleDateString(t.locale)})` : ''));
    }
    onChange?.();
  };

  return createPortal(
    <div className="editor-backdrop" data-testid="settings-dialog">
      <div className="editor settings">
        <div className="editor-head">
          <h4>{t.settings}</h4>
          <button className="icon-btn" data-testid="settings-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        {admin && <TtsNotice t={t} />}
        <PcHomeSetting lang={localStorage.getItem('baustelle_app_lang')} />
        <section className="set-sec">
          <h5><KeyRound size={14} /> {t.licTitle}</h5>
          <div className="device-box" data-testid="device-id-box">
            <Smartphone size={16} />
            <div><small>{t.deviceId}</small><b data-testid="device-id">{deviceId}</b></div>
            <button className="btn btn-outline btn-sm" data-testid="device-id-copy-btn" onClick={() => copyText(t, deviceId)}><Copy size={14} /> {t.copy}</button>
          </div>
          <p className="muted small-hint">{t.licDeviceHint}</p>
          <LicenseStatus t={t} lics={lics} />
          {!admin && !enterpriseLicense() && <PlayStore t={t} onChange={() => { setLics(getLicenses()); onChange?.(); }} />}
          {!admin && <PcUnlock t={t} onChange={() => { setLics(getLicenses()); onChange?.(); }} />}
          <label className="ent-label" htmlFor="license-code-input">{t.entKeyLabel}</label>
          <div className="row">
            <input type="text" id="license-code-input" data-testid="license-code-input" autoCapitalize="off" autoCorrect="off" autoComplete="off" spellCheck={false} placeholder={t.entKeyPlaceholder} value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && activate()} />
            <button className="btn btn-dark btn-sm" data-testid="license-activate-btn" onClick={activate}>{t.licActivate}</button>
          </div>
        </section>
        <section className="set-sec"><StartPref t={t} testid="settings-start-pref" /></section>
        <PolicySettings t={t} />
        <section className="set-sec"><ShowAllFields /></section>
        <details className="md-block" data-testid="settings-features"><summary data-testid="settings-features-toggle">{t.mdFeat}</summary><FeatureProfile t={t} onBranch={onClose} /></details>
        <AiKeySettings t={t} />
        {admin && <TtsAdmin t={t} />}
        {admin && <LicenseGenerator t={t} onExit={() => { leaveOwner(); setAdmin(false); setLics(getLicenses()); onChange?.(); }} />}
        <DataReset t={t} />
        <section className="set-sec about" data-testid="about-section">
          <h5><Info size={14} /> {t.about}</h5>
          <p><b>{APP_INFO.name}</b> · {t.version} {APP_INFO.version}</p>
          <p>{t.publisher}: {APP_INFO.publisher} ({APP_INFO.web})</p>
          <p className="muted">ID: {APP_INFO.packageId}</p>
          <EngineLine />
          <OssLicenses t={t} />
        </section>
      </div>
    </div>,
    document.body,
  );
}
